import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../../config/firebase';
import CourseCard from '../../components/academy/CourseCard';
import { Spinner } from '../../components/ui/Spinner';
import { useBranches } from '../../hooks/useBranches';
import { useAcademySettings } from '../../hooks/useAcademySettings';

const buildMapEmbedSrc = (branch) => {
  const raw = (branch?.googleMapsUrl || '').trim();
  if (raw && /\/maps\/embed/i.test(raw)) return raw;
  const query = [branch?.name, branch?.addressLine, branch?.city]
    .filter(Boolean)
    .join(', ');
  if (!query) return '';
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
};

const buildDirectionsUrl = (branch) => {
  const raw = (branch?.googleMapsUrl || '').trim();
  if (raw) return raw;
  const query = [branch?.name, branch?.addressLine, branch?.city]
    .filter(Boolean)
    .join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || 'Brissbella Academy')}`;
};

const AcademyPage = () => {
  const [courses, setCourses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const { activeList: branches } = useBranches();
  const { academy } = useAcademySettings();

  // Fetch courses from Firebase
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesRef = ref(db, 'courses');
        const snapshot = await get(coursesRef);
        if (snapshot.exists()) {
          setCourses(snapshot.val());
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load courses');
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviewsRef = ref(db, 'feedbacks/reviews');
        const snapshot = await get(reviewsRef);
        if (snapshot.exists()) {
          const reviewsData = snapshot.val();
          setReviews(reviewsData);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Update the Courses Section to use the fetched data
  const renderCoursesSection = () => (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold">OUR COURSES</h2>
          <p className="text-gray-700 mt-4">
            We Offer Range Of Courses From Beginner To Expert Level Of Beauty
            Therapy Education. Check Available Courses Below:
          </p>
        </div>
        {loading ? (
          <div className="flex justify-center">
            <Spinner />
          </div>
        ) : error ? (
          <div className="text-center text-red-600">{error}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {Object.values(courses).map((course) => (
              <CourseCard
                key={course.id}
                title={course.name}
                image={course.imageUrl}
                course={course}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              BRISSBELLA ACADEMY
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Transform Your Passion Into A Career At Brissbella Beauty School -
              Learn From Experts & Master The Latest Techniques In Hair, Makeup
              & Nails. Brissbella Academy Provide High Quality And Professional
              Beauty Therapy Education. Our Curriculums Are Designed To Gain
              Most Demanding And Valuable Skills In Our Modern Classrooms And
              Well Qualified Lecturers.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/images/founder.jpg"
                alt="Founder with awards"
                className="rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-semibold mb-2">
                  Founder & Director
                </h3>
                <h2 className="text-3xl font-bold text-primary-600">
                  Mashi Nehansa
                </h2>
              </div>
              <p className="text-gray-700">
                Mashi Nehansa, the Founder and Director of Brissbella Academy,
                is a renowned Beauty Expert with a Decade of Experience in the
                Industry, with a Passion for Empowering Others to Achieve their
                Full Potential.
              </p>
              <p className="text-gray-700">
                Under Her Leadership, Brissbella Continues to Be a Trusted Name
                in the Beauty World, Providing Students with the Skills and
                Confidence they Need to Succeed.
              </p>
              <div className="space-y-2">
                <p className="text-gray-700">
                  • Diploma in Beauty Culture And Hair Dressing (NAITA)
                </p>
                <p className="text-gray-700">
                  • World Championship in Beauty Culture And Hair Dressing
                  (NAITA)
                </p>
                <p className="text-gray-700">
                  • Herbal Cosmetology, Diploma in Herbal Beauty Product
                  Manufacturing (NVQ Category)
                </p>
                <p className="text-gray-700">
                  • Certificate in Hair And Soap Products Manufacturing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">PARTNERS AND ENDORSEMENTS</h2>
            <p className="text-gray-700 mt-4">
              Brissbella Academy is the Exclusive Partner Of Rica Institute Of
              Aesthetic, Dubai And Progressing Recognition From The Tertiary And
              Vocational Education Commission Of Sri Lanka
            </p>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-12">
            <div className="w-48 sm:w-40 md:w-48 flex items-center justify-center">
              <img
                src="/images/partners/tvec.jpg"
                alt="TVEC"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="w-48 sm:w-40 md:w-48 flex items-center justify-center">
              <img
                src="/images/partners/riva.jpg"
                alt="Rica Institute"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Replace the existing Courses section with the new one */}
      {renderCoursesSection()}

      {/* Student Feedback Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">STUDENT FEEDBACKS</h2>
            <p className="text-gray-700 mt-4">
              See What Our Students Have To Say About Their Learning Experience
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <Spinner />
            </div>
          ) : (
            <div className="relative overflow-hidden">
              <div className="review-slider flex">
                {/* Duplicate reviews array for infinite scroll effect */}
                {[...reviews, ...reviews].map((review, index) => (
                  <div
                    key={`${review.id}-${index}`}
                    className="min-w-[300px] md:min-w-[400px] p-4 animate-slide"
                    style={{
                      animation: `slide ${
                        reviews.length * (window.innerWidth <= 768 ? 3 : 5)
                      }s linear infinite`,
                    }}
                  >
                    <div className="bg-white p-8 rounded-lg shadow-lg review-card-transition h-full">
                      <div className="flex items-center mb-4">
                        <div className="flex-shrink-0">
                          <img
                            src="/images/profile.png"
                            alt={`${review.name}'s profile`}
                            className="w-12 h-12 rounded-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/images/profile.png';
                            }}
                          />
                        </div>
                        <div className="ml-4">
                          <h4 className="font-semibold">{review.name}</h4>
                          <div className="flex text-yellow-400">
                            {[...Array(review.rating)].map((_, i) => (
                              <svg
                                key={i}
                                className="w-4 h-4 fill-current"
                                viewBox="0 0 20 20"
                              >
                                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 review-text">{review.text}</p>
                      <p className="text-sm text-gray-400 mt-4">
                        {review.date}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-center mt-8">
            <p className="text-gray-600">
              Based on {reviews.length} reviews • Overall rating:{' '}
              {reviews[0]?.rating || 5}/5
            </p>
            <a
              href="https://www.google.com/maps/place/Brissbella+Academy+Colombo/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-4 text-primary-600 hover:text-primary-700"
            >
              <span>View all reviews on Google</span>
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">OUR LOCATIONS</h2>
            <p className="text-gray-700 mt-4">
              {branches.length > 0
                ? `${academy?.name || 'Brissbella Academy'} is located at ${branches
                    .map((b) => b.city || b.name)
                    .filter(Boolean)
                    .join(' and ')}. Any student from any region of the country can reach easily.`
                : `${academy?.name || 'Brissbella Academy'} welcomes students from every region of the country.`}
            </p>
          </div>
          {branches.length === 0 ? (
            <p className="text-center text-gray-500">
              Branch locations are being updated. Please check back soon.
            </p>
          ) : (
            <div
              className={`grid gap-8 grid-cols-1 ${
                branches.length === 1
                  ? 'md:grid-cols-1 max-w-3xl mx-auto'
                  : branches.length === 2
                    ? 'md:grid-cols-2'
                    : 'md:grid-cols-2 lg:grid-cols-3'
              }`}
            >
              {branches.map((branch) => {
                const embedSrc = buildMapEmbedSrc(branch);
                const directionsUrl = buildDirectionsUrl(branch);
                const title = `${branch.name || 'Brissbella'} Branch`;
                return (
                  <div
                    key={branch.id}
                    className="rounded-lg overflow-hidden shadow-lg bg-white flex flex-col"
                  >
                    {embedSrc ? (
                      <iframe
                        src={embedSrc}
                        width="100%"
                        height="400"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title={`${branch.name || 'Branch'} Location`}
                        className="w-full"
                      ></iframe>
                    ) : (
                      <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                        Map not available
                      </div>
                    )}
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="font-semibold text-lg mb-2">{title}</h3>
                      {branch.addressLine ? (
                        <p className="text-gray-600">{branch.addressLine}</p>
                      ) : null}
                      {branch.city && !branch.addressLine?.includes(branch.city) ? (
                        <p className="text-gray-600">{branch.city}</p>
                      ) : null}
                      {branch.contactPhone ? (
                        <p className="text-gray-600 mt-2">
                          Contact:{' '}
                          <a
                            className="hover:text-primary-600"
                            href={`tel:${branch.contactPhone.replace(/\s+/g, '')}`}
                          >
                            {branch.contactPhone}
                          </a>
                        </p>
                      ) : null}
                      {branch.contactEmail ? (
                        <p className="text-gray-600">
                          Email:{' '}
                          <a
                            className="hover:text-primary-600"
                            href={`mailto:${branch.contactEmail}`}
                          >
                            {branch.contactEmail}
                          </a>
                        </p>
                      ) : null}
                      <a
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto pt-4 flex items-center justify-center gap-2 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                          />
                        </svg>
                        Get Directions
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Replace Registration Form with CTA Buttons */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              START YOUR JOURNEY TODAY
            </h2>
            <p className="text-xl text-gray-700 mt-4">
              Get in touch with us to learn more about our courses and start
              your career in beauty therapy.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="https://wa.me/94777437130?text=Hi,%20I'm%20interested%20in%20learning%20more%20about%20Brissbella%20Academy%20courses."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-600 text-white py-4 px-6 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp Us
            </a>
            <a
              href="tel:+94777437130"
              className="flex items-center justify-center gap-2 bg-primary-600 text-white py-4 px-6 rounded-lg text-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              Call Us
            </a>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 border-2 border-primary-600 text-primary-600 py-4 px-6 rounded-lg text-lg font-medium hover:bg-primary-50 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AcademyPage;
